/**
 * MACRO #32 — Genesis / Founding Territory read client.
 *
 * Reads the backend-authoritative Genesis provenance. The provenance qualifies
 * the TERRITORY/parcel itself (GENESIS #N / FOUNDING TERRITORY); the current
 * owner is reported SEPARATELY and NEVER treated as the platform founder.
 * Read-only — there are no public mutation endpoints.
 */
import { API_BASE } from "./api";

export interface GenesisFirstAcquisition {
  block_number: number | null;
  log_index: number | null;
  tx_hash: string | null;
  kind: string | null;
}

export interface GenesisTerritory {
  parcel_id: number;
  is_genesis: boolean;
  set_status: "DRAFT" | "FINALIZED";
  genesis_ordinal: number | null; // definitive ONLY when finalized
  preview_ordinal: number | null; // provisional (DRAFT) — never definitive
  ordinal_provisional: boolean;
  acquisition_status: "RESOLVED" | "PENDING";
  first_acquisition: GenesisFirstAcquisition | null;
  provenance: string;
  current_owner: string | null;
  designated_at?: string | null;
  finalized_at?: string | null;
}

export interface GenesisOverview {
  status: "DRAFT" | "FINALIZED";
  integrity_state: string;
  draft_cap: number;
  member_count: number;
  resolved_count: number;
  pending_count: number;
  finalized_at: string | null;
  finalized_by: string | null;
  territories: GenesisTerritory[];
}

export async function fetchGenesisOverview(signal?: AbortSignal): Promise<GenesisOverview> {
  const resp = await fetch(`${API_BASE}/api/v1/genesis`, { cache: "no-store", signal });
  if (!resp.ok) throw new Error(`genesis_overview_failed_${resp.status}`);
  return (await resp.json()) as GenesisOverview;
}

export async function fetchGenesisParcel(parcelId: number, signal?: AbortSignal): Promise<GenesisTerritory> {
  const resp = await fetch(`${API_BASE}/api/v1/genesis/parcel/${encodeURIComponent(String(parcelId))}`, {
    cache: "no-store",
    signal,
  });
  if (!resp.ok) throw new Error(`genesis_parcel_failed_${resp.status}`);
  return (await resp.json()) as GenesisTerritory;
}
